import type { IconWeight } from '@phosphor-icons/react'
import { type ComponentType, type ReactElement, type ReactNode, useState } from 'react'
import { cn } from 'app/lib/utils'

/**
 * One exit zone: a drop target at work, and a doorway into the stage at rest.
 *
 * Generic over what a board's exit means (contact boards write a pipeline stage,
 * the treatment board writes a case's scheduled or lost state), so it knows
 * nothing about contacts, cases or pipelines. The count badge is `children`,
 * since counting who is already at the stage is the caller's own query.
 */
export function ListBoardExit(props: {
  /** Set on a zone a finger drag can end on. Emitted as `data-board-drop`. */
  dropKey?: string
  icon: ComponentType<{ size?: string | number; weight?: IconWeight; className?: string }>
  resting: string
  dropping: string
  /** Classes while a card is in the air somewhere, and while it is over this zone. */
  armedClass: string
  overClass: string
  /** A card is in the air somewhere on the board. */
  armed: boolean
  /** A finger drag is over this zone (the shell tracks that, not a drag event). */
  touchOver?: boolean
  /** The id the board believes is in the air, for a drop whose dataTransfer is empty. */
  fallbackId?: string
  onDrop: (id: string) => void
  /** Pressing the zone lists who is already there. Absent: the zone is not a button. */
  onOpenList?: () => void
  title?: string
  children?: ReactNode
}): ReactElement {
  const {
    dropKey,
    icon: Icon,
    resting,
    dropping,
    armedClass,
    overClass,
    armed,
    touchOver,
    fallbackId,
    onDrop,
    onOpenList,
    title,
    children,
  } = props
  const [dragOver, setDragOver] = useState(false)
  const over = dragOver || !!touchOver
  const openable = !!onOpenList
  return (
    <div
      data-board-drop={dropKey}
      role={openable ? `button` : undefined}
      tabIndex={openable ? 0 : undefined}
      title={openable ? title : undefined}
      onClick={openable ? onOpenList : undefined}
      onKeyDown={
        openable
          ? (event) => {
              if (event.key !== `Enter` && event.key !== ` `) return
              event.preventDefault()
              onOpenList()
            }
          : undefined
      }
      onDragOver={(event) => {
        event.preventDefault()
        setDragOver(true)
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(event) => {
        setDragOver(false)
        const id = event.dataTransfer.getData(`text/plain`) || fallbackId
        if (id) onDrop(id)
      }}
      className={cn(
        `group flex flex-1 items-center justify-center gap-2 rounded-xl border-2 border-dashed px-4 py-3.5 text-center text-body-sm transition`,
        over ? overClass : armed ? armedClass : `border-strong bg-surface/60 text-subtle`,
        // Only at rest: while a card is in the air the zone is a target, and
        // dressing it as a link then would be reading the gesture wrongly.
        openable && !over && !armed && `cursor-pointer hover:border-strong hover:bg-surface hover:text-strong`,
      )}
    >
      <Icon size={16} weight="bold" className="shrink-0" />
      <span>{over ? dropping : resting}</span>
      {children}
    </div>
  )
}
